import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../supabaseClient";

const API_BASE = "/api/transcript";

function fmt(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function Avatar({ src, name }) {
  const [err, setErr] = useState(false);
  const initials = (name || "?")[0].toUpperCase();
  if (!src || err) {
    return (
      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-[#ffd978]/20 border border-[#ffd978]/30 flex items-center justify-center text-sm font-bold text-[#ffd978]">
        {initials}
      </div>
    );
  }
  return (
    <img
      src={src} alt={name} onError={() => setErr(true)}
      className="flex-shrink-0 w-9 h-9 rounded-full object-cover border border-white/10"
    />
  );
}

function Message({ msg, prevMsg }) {
  const grouped =
    prevMsg &&
    prevMsg.author_id === msg.author_id &&
    new Date(msg.timestamp) - new Date(prevMsg.timestamp) < 5 * 60 * 1000;

  return (
    <div className={`flex gap-3 ${grouped ? "mt-0.5" : "mt-5"}`}>
      <div className="w-9 flex-shrink-0">
        {!grouped && <Avatar src={msg.author_avatar} name={msg.author_name} />}
      </div>
      <div className="flex-1 min-w-0">
        {!grouped && (
          <div className="flex items-baseline gap-2 mb-0.5">
            <span className={`font-semibold text-sm ${msg.bot ? "text-[#5865F2]" : "text-white"}`}>
              {msg.author_name}
              {msg.bot && (
                <span className="ml-1.5 text-[10px] bg-[#5865F2] text-white px-1 py-0.5 rounded font-bold align-middle">
                  BOT
                </span>
              )}
            </span>
            <span className="text-[11px] text-white/30">{fmt(msg.timestamp)}</span>
          </div>
        )}
        {msg.content && (
          <p className="text-sm text-white/80 whitespace-pre-wrap break-words leading-relaxed">
            {msg.content}
          </p>
        )}
        {msg.attachments?.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-2">
            {msg.attachments.map((a, i) => {
              const isImg = /\.(png|jpe?g|gif|webp|svg)$/i.test(a.filename);
              return isImg ? (
                <a key={i} href={a.url} target="_blank" rel="noreferrer">
                  <img src={a.url} alt={a.filename}
                    className="max-h-48 max-w-xs rounded-lg border border-white/10 object-contain" />
                </a>
              ) : (
                <a key={i} href={a.url} target="_blank" rel="noreferrer"
                  className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-[#ffd978] hover:bg-white/10 transition">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM6 20V4h5v7h7v9H6z"/>
                  </svg>
                  {a.filename}
                </a>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Transcript() {
  const { slug }              = useParams();
  const { session }           = useAuth();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);   // null | 'login' | 'denied' | string
  const [signingIn, setSigningIn] = useState(false);

  // Fetch once we have a session
  useEffect(() => {
    if (!slug) return;
    if (session === undefined) return;  // still resolving
    if (!session) { setError('login'); return; }

    setLoading(true);
    setError(null);

    fetch(`${API_BASE}?slug=${encodeURIComponent(slug)}`, {
      headers: { Authorization: `Bearer ${session.access_token}` },
    })
      .then(async r => {
        if (r.status === 401) throw new Error('login');
        if (r.status === 403) throw new Error('denied');
        if (!r.ok) throw new Error(r.status === 404 ? 'Transcript not found.' : 'Failed to load transcript.');
        return r.json();
      })
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [slug, session]);

  async function handleLogin() {
    setSigningIn(true);
    await supabase.auth.signInWithOAuth({
      provider: 'discord',
      options: { redirectTo: window.location.href },
    });
  }

  // ── Loading session ───────────────────────────────────────────────────────
  if (session === undefined) {
    return (
      <main className="min-h-screen bg-[#050505] text-white">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#ffd978]/40 border-t-[#ffd978] rounded-full animate-spin" />
        </div>
      </main>
    );
  }

  // ── Not logged in ─────────────────────────────────────────────────────────
  if (error === 'login' || !session) {
    return (
      <main className="min-h-screen bg-[#050505] text-white">
        <Header />
        <section className="flex min-h-screen items-center justify-center px-6 pt-24 pb-20">
          <div className="w-full max-w-md text-center">
            <p className="font-black uppercase text-[#ffb56f] mb-2">Ticket Transcript</p>
            <h1 className="text-3xl font-black text-[#ffd978] mb-3">{slug}</h1>
            <p className="text-white/50 text-sm mb-8">
              Sign in with Discord to verify you were part of this ticket.
            </p>
            <div className="rounded-2xl border-2 border-[#ffd978] bg-black/60 p-8 shadow-[0_0_35px_rgba(255,143,107,0.2)]">
              <button
                onClick={handleLogin}
                disabled={signingIn}
                className="flex w-full items-center justify-center gap-3 rounded-xl bg-[#5865F2] px-6 py-4 font-black text-white transition hover:bg-[#4752C4] disabled:opacity-50"
              >
                <svg width="24" height="18" viewBox="0 0 24 18" fill="currentColor">
                  <path d="M20.317 1.492a19.825 19.825 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.294 18.294 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 1.492a.07.07 0 0 0-.032.027C.533 6.093-.32 10.555.099 14.961a.08.08 0 0 0 .031.055 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.3 12.3 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.442a.061.061 0 0 0-.031-.028z"/>
                </svg>
                {signingIn ? 'Redirecting...' : 'Continue with Discord'}
              </button>
            </div>
          </div>
        </section>
      </main>
    );
  }

  // ── Access denied ─────────────────────────────────────────────────────────
  if (error === 'denied') {
    return (
      <main className="min-h-screen bg-[#050505] text-white">
        <Header />
        <section className="flex min-h-screen items-center justify-center px-6 pt-24 pb-20">
          <div className="text-center max-w-md">
            <h1 className="text-2xl font-black text-red-400 mb-3">Access Denied</h1>
            <p className="text-white/50 text-sm">
              You weren't part of this ticket and don't have staff access.
            </p>
            <button
              onClick={() => supabase.auth.signOut()}
              className="mt-6 text-xs text-white/30 hover:text-white/60 underline transition"
            >
              Sign out
            </button>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#050505] text-white">
      <Header />
      <section className="max-w-3xl mx-auto px-4 pt-28 pb-20">

        {(loading || (!data && !error)) && (
          <div className="flex items-center justify-center h-40">
            <div className="w-8 h-8 border-2 border-[#ffd978]/40 border-t-[#ffd978] rounded-full animate-spin" />
          </div>
        )}

        {error && error !== 'login' && error !== 'denied' && (
          <div className="text-center py-20">
            <p className="text-4xl mb-4">📄</p>
            <h1 className="text-xl font-bold text-white/60">{error}</h1>
            <p className="text-sm text-white/30 mt-2">{slug}</p>
          </div>
        )}

        {data && (
          <>
            {/* Header card */}
            <div className="rounded-2xl border border-[#ffd978]/30 bg-black/50 p-6 mb-6 shadow-[0_0_30px_rgba(255,143,107,0.08)]">
              <p className="text-xs font-bold uppercase tracking-widest text-[#ffb56f] mb-1">Ticket Transcript</p>
              <h1 className="text-2xl font-black text-[#ffd978] mb-4">#{data.ticket_name}</h1>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-white/40 text-xs mb-0.5">Opened by</p>
                  <p className="font-semibold text-white truncate">{data.opener_name || 'Unknown'}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-white/40 text-xs mb-0.5">Closed by</p>
                  <p className="font-semibold text-white truncate">{data.closer_name || 'Unknown'}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-white/40 text-xs mb-0.5">Closed at</p>
                  <p className="font-semibold text-white">{fmt(data.closed_at)}</p>
                </div>
                {data.open_reason && (
                  <div className="bg-white/5 rounded-xl p-3 col-span-2 sm:col-span-3">
                    <p className="text-white/40 text-xs mb-0.5">Open reason</p>
                    <p className="font-semibold text-white">{data.open_reason}</p>
                  </div>
                )}
                {data.close_reason && (
                  <div className="bg-white/5 rounded-xl p-3 col-span-2 sm:col-span-3">
                    <p className="text-white/40 text-xs mb-0.5">Close reason</p>
                    <p className="font-semibold text-white">{data.close_reason}</p>
                  </div>
                )}
              </div>

              <p className="text-xs text-white/20 mt-4">
                {data.messages?.length ?? 0} message{data.messages?.length !== 1 ? 's' : ''} · {slug}
              </p>
            </div>

            {/* Messages */}
            <div className="rounded-2xl border border-white/10 bg-black/40 p-5">
              {(!data.messages || data.messages.length === 0) ? (
                <p className="text-center text-white/30 py-8 text-sm">No messages in this transcript.</p>
              ) : (
                data.messages.map((msg, i) => (
                  <Message key={msg.id || i} msg={msg} prevMsg={i > 0 ? data.messages[i - 1] : null} />
                ))
              )}
            </div>
          </>
        )}
      </section>
    </main>
  );
}
